/**
 * **ST-23 — one story's views and viewers, day by day.**
 *
 * Jesse, 6 Oct: *"I also would like to click into [a story] and see views per
 * day."* Opened from the row's views figure on Public and Saved Stories.
 *
 * A native `<dialog>`, like the thumbnail lightbox: `showModal()` gives Esc,
 * focus trapping and an inert page for free, and the browser hands focus back
 * to the figure that opened it.
 *
 * Two rules the chart must not break:
 * - **Viewers is never totalled.** It counts people per day; adding days counts
 *   someone who watched on two days twice. The table shows it per day only.
 * - **Two series carry a legend and solid-vs-dashed** — the blue↔mint pair is
 *   5.9 apart for tritan vision, so identity is never colour alone.
 *   **No direct end labels** (Nilay, 7 Oct): both lines finish near zero, so
 *   the labels sat on the axis repeating the legend. A recorded departure from
 *   `DESIGN-SYSTEM.md`'s two-series rule, for this chart only.
 */
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { api } from '../lib/api'
import { formatAxisDate, formatReadoutDate } from '../lib/dates'
import { abbreviate, formatNumber } from '../lib/numbers'
import { storiesLink } from '../lib/rawFilters'
import { formatCount, TipCard } from './ChartTip'
import { InfoDot } from './InfoDot'
import { todayUtc } from './SendToRawData'
import { Segmented } from './Tabs'
import { Note, Skeleton } from './ui'

const SERIES = [
  { key: 'views', name: 'Views', colour: 'var(--data-1)', dash: undefined },
  { key: 'viewers', name: 'Viewers', colour: 'var(--data-2)', dash: '5 4' },
] as const

/** What the panel needs from a row — a story's or a spotlight's. */
export interface DailyTarget {
  asset_id: string
  handle: string
  display_label: string
  title: string | null
  published_at: string | null
}

const SAVED_CAVEAT =
  'Per-Story Saved Figures Run Well Below Snap’s Channel Totals. Read the Shape, Not the Size.'

export function StoryDailyPanel({
  row,
  assetType,
  onClose,
}: {
  row: DailyTarget | null
  assetType: 'STORY' | 'SAVED_STORY' | 'SPOTLIGHT'
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  // Nilay, 7 Oct: a public story's views land almost entirely in its first two
  // days, so First 5 Days is offered — public stories only. A saved story is
  // watched steadily for weeks, and there the whole history is the point.
  const [span, setSpan] = useState<'first5' | 'all'>('all')

  useEffect(() => {
    const dialog = ref.current
    if (!dialog || !row) return
    // jsdom has no showModal; the attribute is what it understands.
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal()
    } else {
      dialog.setAttribute('open', '')
    }
    // showModal() focuses the first focusable element — the caveat's info dot,
    // whose tooltip then opened unasked (seen 7 Oct). React's `autoFocus` does
    // not reach the dialog's own focus step, so focus Close explicitly.
    closeButton.current?.focus()
    setSpan('all')
  }, [row])

  const daily = useQuery({
    queryKey: ['story-daily', row?.asset_id],
    queryFn: () =>
      assetType === 'SPOTLIGHT' ? api.spotlightDaily(row!.asset_id) : api.storyDaily(row!.asset_id),
    enabled: row !== null,
  })

  const close = () => {
    ref.current?.close?.()
    onClose()
  }

  if (!row) return null
  const all = daily.data?.points ?? []
  const points = span === 'first5' ? all.slice(0, 5) : all
  const title = row.title || `${row.display_label} Episode`

  return (
    <dialog
      ref={ref}
      className="panel-dialog"
      aria-label={`Views per Day: ${title}`}
      onClose={onClose}
      onClick={(event) => {
        // The backdrop is the dialog element itself.
        if (event.target === ref.current) close()
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs" style={{ color: 'var(--text-label)' }}>
            Views per Day · {row.display_label}
          </div>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h2>
          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Published {formatReadoutDate(row.published_at)} · Snap’s Day Is UTC
            {assetType === 'SAVED_STORY' && <InfoDot note={SAVED_CAVEAT} />}
          </div>
          {daily.data?.history_starts && (
            // A 2023 spotlight has daily figures only from 20 Jul 2026; without
            // this the chart reads as if the post began there.
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Daily Figures Start {formatReadoutDate(daily.data.history_starts)}, When Our
              Collection Began. Earlier Days Are Not Available.
            </div>
          )}
        </div>
        <button
          ref={closeButton}
          type="button"
          className="lightbox-close-inline"
          aria-label="Close"
          onClick={close}
        >
          ×
        </button>
      </div>

      {daily.isLoading ? (
        <Skeleton rows={4} />
      ) : daily.error ? (
        <Note tone="error">{(daily.error as Error).message}</Note>
      ) : points.length === 0 ? (
        <Note>No Daily Figures Collected for This Story.</Note>
      ) : (
        <>
          {assetType === 'STORY' && all.length > 5 && (
            <div className="mt-3 flex justify-end">
              <Segmented
                label="Show"
                options={[
                  { key: 'first5', label: 'First 5 Days' },
                  { key: 'all', label: `All ${all.length} Days` },
                ]}
                active={span}
                onChange={setSpan}
              />
            </div>
          )}
          <div style={{ height: 220, flex: 'none' }} className="mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="stat_date"
                  tickFormatter={formatAxisDate}
                  tick={{ fontSize: 11, fill: 'var(--text-label)' }}
                  stroke="var(--border)"
                  minTickGap={24}
                />
                <YAxis
                  tickFormatter={(value: number) => abbreviate(value)}
                  tick={{ fontSize: 11, fill: 'var(--text-label)' }}
                  stroke="var(--border)"
                  width={48}
                />
                <Tooltip
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <TipCard
                        title={formatReadoutDate(String(label))}
                        rows={SERIES.map((series) => {
                          const value = payload.find((p) => p.dataKey === series.key)?.value
                          return {
                            label: series.name,
                            value: typeof value === 'number' ? formatCount(value) : '—',
                          }
                        })}
                      />
                    ) : null
                  }
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {SERIES.map((series) => (
                  <Line
                    key={series.key}
                    dataKey={series.key}
                    name={series.name}
                    stroke={series.colour}
                    strokeWidth={2}
                    strokeDasharray={series.dash}
                    dot={points.length === 1}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Only the table scrolls: the chart stays in view beside the
              numbers it draws (Nilay, 7 Oct). */}
          <div className="panel-dialog-scroll mt-3">
          <table className="w-full text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr style={{ color: 'var(--text-label)', position: 'sticky', top: 0, background: 'var(--bg-surface)' }}>
                <th className="text-left font-normal">Day (UTC)</th>
                <th className="text-right font-normal">Views</th>
                <th className="text-right font-normal">
                  Viewers
                  <InfoDot note="People Who Watched on That Day." />
                </th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.stat_date}>
                  <td>{formatReadoutDate(point.stat_date)}</td>
                  <td className="text-right">{point.views === null ? '—' : formatNumber(point.views)}</td>
                  <td className="text-right">{point.viewers === null ? '—' : formatNumber(point.viewers)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      <div className="mt-3 text-xs">
        <Link to={storiesLink([row], todayUtc())} onClick={close} style={{ color: 'var(--brand-ink)' }}>
          Open These Rows in Raw Data
        </Link>
      </div>
    </dialog>
  )
}
